import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Check } from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import PlanExpiredBanner from '../../components/billing/PlanExpiredBanner';
import { authService, clientService, sessionService, api } from '../../services';
import { MAX_UPLOAD_MB } from '../../utils/uploadLimits';

/**
 * Create Session Wizard - Minimalist Design
 * Clean, white-dominant design with line icons
 * Following "less is more" philosophy
 */

const CreateSessionPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preSelectedTrainerId = searchParams.get('trainer');
  const [currentStep, setCurrentStep] = useState(1);
  const [user, setUser] = useState(null);
  /**
   * Effective per-session participant cap from the API. An admin can raise one org
   * above its plan default (Organization.limits.maxParticipantsOverride); the backend
   * honours it everywhere, but this page derived the cap from a hardcoded plan map,
   * so an org granted 75 was still shown "Fixed at 30" and blocked at 30.
   * null until loaded, then it replaces the plan-map value.
   */
  const [effectiveLimit, setEffectiveLimit] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdSession, setCreatedSession] = useState(null);
  const [planStatus, setPlanStatus] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sessionType: 'public',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    timezone: 'Asia/Kolkata', // India Standard Time
    trainerId: '',
    expectedParticipants: 30,
    maxParticipants: 30,
    workbooks: [],
    enableOverallFeedback: true, // Enable overall session feedback by default

    // Phase 2: pre-session config
    preSessionEnabled: false,
    preSessionMaterials: [],
    selfAssessmentEnabled: false,
    selfAssessmentQuestions: [],
    challengeCaptureEnabled: true,
    expectationCaptureEnabled: true,

    // Phase 2: post-session config
    postSessionEnabled: false,
    actionPlanTrackingEnabled: true,
    managerReinforcementEnabled: true,
    reflectionCyclesEnabled: true,
    reflectionIntervals: [30, 60, 90],
    reflectionQuestions: [
      { questionText: 'What action did you implement?', order: 0 },
      { questionText: 'What result did you observe?', order: 1 },
      { questionText: 'What challenge remains?', order: 2 },
      { questionText: 'What support do you need?', order: 3 },
    ],
  });

  /**
   * Raw text for the reflection-intervals field, held separately from the parsed array.
   *
   * Rendering `reflectionIntervals.join(', ')` and reparsing on every keystroke destroyed
   * half-typed input: "30," split to ["30",""], the empty half became NaN, the filter
   * dropped it, and join() returned "30" — deleting the comma on the keypress that typed
   * it, so a second interval could never be entered.
   */
  const [reflectionIntervalsText, setReflectionIntervalsText] = useState('30, 60, 90');

  // Separate state for 12-hour time format with AM/PM
  const [timeData, setTimeData] = useState({
    startHour: '09',
    startMinute: '00',
    startPeriod: 'AM',
    endHour: '10',
    endMinute: '00',
    endPeriod: 'AM',
  });

  const [errors, setErrors] = useState({});

  // Track if form has been modified (dirty state)
  const [isDirty, setIsDirty] = useState(false);
  const [showBackConfirm, setShowBackConfirm] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Check if form has any data entered
  const checkIfDirty = () => {
    return formData.name !== '' ||
           formData.description !== '' ||
           formData.startDate !== '' ||
           formData.trainerId !== '' ||
           formData.workbooks.length > 0;
  };

  // Handle back navigation with unsaved changes check
  const handleBackNavigation = (path) => {
    if (checkIfDirty()) {
      setPendingNavigation(path);
      setShowBackConfirm(true);
    } else {
      navigate(path);
    }
  };

  // Confirm leaving without saving
  const confirmLeave = () => {
    setShowBackConfirm(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  };

  const applyPlanLimits = (userData) => {
    const participantLimits = { free: 30, pro: 30, pro_plus: 100, enterprise_starter: 200, enterprise_scale: -1 };
    const userPlan = userData.primaryOrganization?.plan || userData.organization?.plan || 'free';
    const planLimit = participantLimits[userPlan] || 30;
    setFormData((prev) => ({
      ...prev,
      // maxParticipants is set authoritatively from the API (getSubscription),
      // which reflects any admin participant-cap override — don't clobber it here.
      expectedParticipants: planLimit === -1 ? 30 : Math.min(30, planLimit),
    }));
  };

  // applyPlanLimits seeds the form from the plan map on mount; once the API cap
  // resolves it supersedes that, so re-seed both fields from the real value.
  useEffect(() => {
    if (typeof effectiveLimit !== 'number' || effectiveLimit === 0) return;
    setFormData((prev) => ({
      ...prev,
      maxParticipants: effectiveLimit === -1 ? 500 : effectiveLimit,
      expectedParticipants: effectiveLimit === -1 ? prev.expectedParticipants : Math.min(prev.expectedParticipants, effectiveLimit),
    }));
  }, [effectiveLimit]);

  useEffect(() => {
    // Load cached user first for fast render
    const cachedData = localStorage.getItem('user');
    if (cachedData) {
      const parsedUser = JSON.parse(cachedData);
      setUser(parsedUser);
      applyPlanLimits(parsedUser);
    }

    // Fetch fresh user data from API to get up-to-date plan info
    authService.getProfile().then((res) => {
      if (res.success && res.data) {
        const freshUser = res.data;
        setUser(freshUser);
        applyPlanLimits(freshUser);
        // Update localStorage so other pages also have fresh data
        localStorage.setItem('user', JSON.stringify(freshUser));
      }
      // getMe returns planStatus alongside user
      if (res?.planStatus) setPlanStatus(res.planStatus);
    }).catch(() => { /* ignore - cached data still works */ });

    // Resolve the real participant cap (plan default, or an admin override).
    clientService.getSubscription().then((res) => {
      const lim = res?.data?.features?.participants ?? res?.data?.usage?.participants?.limit;
      if (typeof lim === 'number' && lim !== 0) setEffectiveLimit(lim);
    }).catch(() => { /* non-fatal — falls back to the plan map */ });

    // Also fetch dashboard stats — its planStatus is the authoritative server-side check.
    clientService.getDashboardStats().then((res) => {
      if (res?.success) setPlanStatus(res.data?.planStatus || res.planStatus || null);
    }).catch(() => { /* non-fatal */ });

    fetchTrainers();

    // Initialize times with defaults (9:00 AM - 10:00 AM)
    setFormData((prev) => ({
      ...prev,
      startTime: '09:00',
      endTime: '10:00',
    }));
  }, []);

  const fetchTrainers = async () => {
    try {
      const response = await clientService.getTrainers({ status: 'active' });
      if (response.success && response.data.trainers) {
        // Only show active trainers (trainerStatus !== 'inactive') in the dropdown
        const transformedTrainers = response.data.trainers
          .filter((trainer) => trainer.trainerStatus !== 'inactive')
          .map((trainer) => ({
            id: trainer._id,
            fullName: `${trainer.firstName} ${trainer.lastName}`,
            email: trainer.email,
            expertise: trainer.expertise || [],
            yearsOfExperience: trainer.yearsOfExperience || 0,
            avgRating: trainer.averageRating || 0,
            totalSessions: trainer.sessionsCreated || 0,
          }));
        setTrainers(transformedTrainers);

        // Pre-select trainer if passed via URL query parameter
        if (preSelectedTrainerId) {
          const trainerExists = transformedTrainers.some(t => t.id === preSelectedTrainerId);
          if (trainerExists) {
            setFormData(prev => ({ ...prev, trainerId: preSelectedTrainerId }));
          }
        }
      }
    } catch (error) {
      console.error('Error fetching trainers:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Clamp participant fields to plan limits
    if (name === 'maxParticipants') {
      const limit = getParticipantLimit();
      const num = Number(value);
      if (limit !== -1 && num > limit) {
        setFormData((prev) => ({ ...prev, [name]: limit }));
        setErrors((prev) => ({ ...prev, maxParticipants: `Maximum ${limit} for your plan` }));
        return;
      }
    }
    if (name === 'expectedParticipants') {
      const limit = getParticipantLimit();
      // The cap is the authority. maxParticipants is display-only and mirrors it,
      // but a plan-map seeder can land AFTER the real cap resolves and leave 30 in
      // form state — clamping to that told users "Cannot exceed 30" next to a
      // field reading 75.
      const clampTo = limit === -1 ? 500 : limit;
      const num = Number(value);
      if (num > clampTo) {
        setFormData((prev) => ({ ...prev, [name]: clampTo }));
        setErrors((prev) => ({ ...prev, expectedParticipants: `Cannot exceed ${clampTo}` }));
        return;
      }
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }

    // Real-time validation for date field - show warning if past date is selected
    if (name === 'date' && value) {
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        setErrors((prev) => ({ ...prev, date: 'Cannot schedule session in the past' }));
      } else if (selectedDate.getTime() === today.getTime() && formData.startTime) {
        // If date is today and we have a start time, validate it
        const [hours, mins] = formData.startTime.split(':').map(Number);
        const now = new Date();
        const selectedDateTime = new Date();
        selectedDateTime.setHours(hours, mins, 0, 0);

        if (selectedDateTime < now) {
          setErrors((prev) => ({ ...prev, startTime: 'Start time has already passed' }));
        } else {
          // Clear start time error if time is valid
          setErrors((prev) => ({ ...prev, startTime: '' }));
        }
      } else {
        // Clear start time error if date is in future
        setErrors((prev) => ({ ...prev, startTime: '' }));
      }
    }
  };

  // Convert 12-hour to 24-hour format
  const convertTo24Hour = (hour, minute, period) => {
    let h = parseInt(hour, 10);
    if (period === 'AM') {
      if (h === 12) h = 0;
    } else {
      if (h !== 12) h = h + 12;
    }
    return `${h.toString().padStart(2, '0')}:${minute}`;
  };

  // Create ISO datetime string from date and time in specified timezone
  // The time entered is in the SELECTED timezone, we need to convert to UTC
  const createISODateTime = (dateStr, timeStr, tz) => {
    if (!dateStr || !timeStr) return null;
    try {
      const [hours, minutes] = timeStr.split(':').map(Number);
      const [year, month, day] = dateStr.split('-').map(Number);

      // Get timezone offset by comparing formatted time in target TZ vs UTC
      // Create a reference date in UTC
      const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0));

      // Get what this UTC time looks like in the target timezone
      const tzParts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).formatToParts(utcDate);

      const getPart = (type) => {
        const part = tzParts.find((p) => p.type === type);
        return part ? parseInt(part.value, 10) : 0;
      };

      const tzHour = getPart('hour');
      const tzMinute = getPart('minute');
      const tzDay = getPart('day');
      const tzMonth = getPart('month');
      const tzYear = getPart('year');

      // Calculate the difference between what user entered and what TZ shows
      // User entered 12:15 in TZ X, but utcDate formatted in TZ X shows different time
      // The difference tells us the offset
      const userEnteredMs = new Date(year, month - 1, day, hours, minutes, 0).getTime();
      const tzDisplayMs = new Date(tzYear, tzMonth - 1, tzDay, tzHour, tzMinute, 0).getTime();
      const offsetMs = userEnteredMs - tzDisplayMs;

      // Apply offset to get correct UTC time
      const correctUtc = new Date(utcDate.getTime() + offsetMs);
      return correctUtc.toISOString();
    } catch (e) {
      console.error('Error creating ISO datetime:', e);
      // Fallback: just combine date and time (treating as local)
      return `${dateStr}T${timeStr}:00.000Z`;
    }
  };

  // Handle time changes for manual input (limit to 2 digits)
  const handleTimeChange = (field, value) => {
    // Limit input to 2 characters max for hour and minute fields
    const limitedValue = (field.includes('Hour') || field.includes('Minute'))
      ? value.toString().slice(0, 2)
      : value;
    const newTimeData = { ...timeData, [field]: limitedValue };
    setTimeData(newTimeData);

    // Update formData with 24-hour format
    const startTime24 = convertTo24Hour(
      field.startsWith('start') && field.includes('Hour') ? limitedValue : newTimeData.startHour,
      field.startsWith('start') && field.includes('Minute') ? limitedValue : newTimeData.startMinute,
      field === 'startPeriod' ? limitedValue : newTimeData.startPeriod
    );
    const endTime24 = convertTo24Hour(
      field.startsWith('end') && field.includes('Hour') ? limitedValue : newTimeData.endHour,
      field.startsWith('end') && field.includes('Minute') ? limitedValue : newTimeData.endMinute,
      field === 'endPeriod' ? limitedValue : newTimeData.endPeriod
    );

    setFormData((prev) => ({
      ...prev,
      startTime: startTime24,
      endTime: endTime24,
    }));

    // Clear time errors first
    setErrors((prev) => ({ ...prev, startTime: '', endTime: '' }));

    // Real-time validation: Check if start time is in the past when start date is today
    if (formData.startDate) {
      const selectedDate = new Date(formData.startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);

      // If date is today, check if start time is in the past
      if (selectedDate.getTime() === today.getTime()) {
        const [hours, mins] = startTime24.split(':').map(Number);
        const now = new Date();
        const selectedDateTime = new Date();
        selectedDateTime.setHours(hours, mins, 0, 0);

        if (selectedDateTime < now) {
          setErrors((prev) => ({ ...prev, startTime: 'Start time has already passed' }));
        }
      }
    }
  };

  // Handle time blur to validate and format the value
  const handleTimeBlur = (field, value) => {
    let formattedValue = value;

    if (field.includes('Hour')) {
      const num = parseInt(value, 10);
      if (isNaN(num) || num < 1) {
        formattedValue = '01';
      } else if (num > 12) {
        formattedValue = '12';
      } else {
        formattedValue = num.toString().padStart(2, '0');
      }
    }

    if (field.includes('Minute')) {
      const num = parseInt(value, 10);
      if (isNaN(num) || num < 0) {
        formattedValue = '00';
      } else if (num > 59) {
        formattedValue = '59';
      } else {
        formattedValue = num.toString().padStart(2, '0');
      }
    }

    if (formattedValue !== value) {
      handleTimeChange(field, formattedValue);
    }
  };

  // Generate hour options (01-12)
  const hourOptions = Array.from({ length: 12 }, (_, i) => {
    const hour = i + 1;
    return hour.toString().padStart(2, '0');
  });

  // Generate minute options (00, 05, 10, ... 55) - 5 minute intervals for easier selection
  const minuteOptions = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Session name is required';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    // End date and time fields are optional - only validate if provided

    // Check if start date is in the past
    if (formData.startDate) {
      const selectedDate = new Date(formData.startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        newErrors.startDate = 'Cannot schedule session in the past';
      }

      // If start date is today, also check if start time is in the past
      if (formData.startTime && selectedDate.getTime() === today.getTime()) {
        const [hours, mins] = formData.startTime.split(':').map(Number);
        const now = new Date();
        const selectedDateTime = new Date(selectedDate);
        selectedDateTime.setHours(hours, mins, 0, 0);

        if (selectedDateTime < now) {
          newErrors.startTime = 'Start time has already passed';
        }
      }
    }

    // Validate end date if provided - must be on or after start date
    if (formData.endDate && formData.startDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      if (endDate < startDate) {
        newErrors.endDate = 'End date cannot be before start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getParticipantLimit = () => {
    // Prefer the API cap — it reflects any admin participant-cap override.
    if (typeof effectiveLimit === 'number' && effectiveLimit !== 0) return effectiveLimit;
    const participantLimits = { free: 30, pro: 30, pro_plus: 100, enterprise_starter: 200, enterprise_scale: -1 };
    const userPlan = user?.primaryOrganization?.plan || user?.organization?.plan || 'free';
    return participantLimits[userPlan] || 30;
  };

  const isUnlimitedParticipants = () => getParticipantLimit() === -1;

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.trainerId) newErrors.trainerId = 'Please select a trainer';
    if (formData.expectedParticipants < 1) newErrors.expectedParticipants = 'Must be at least 1';
    const planLimit = getParticipantLimit();
    // Derive the cap here rather than reading formData.maxParticipants: an async
    // load effect can overwrite that with a stale plan-map value after the real
    // cap has resolved, which made a 50 under a 75 cap fail validation.
    const cap = planLimit === -1 ? 500 : planLimit;
    if (Number(formData.expectedParticipants) > cap) {
      newErrors.expectedParticipants = `Cannot exceed ${cap}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleCreateSession = async () => {
    // Re-run step-1 validation on final submit. The user may have stayed on
    // step 2 long enough for their original start time to fall into the past
    // (we don't re-check from step 1 between steps), so a stale "valid"
    // state at step 1 isn't trustworthy at submit time. If it fails, jump
    // them back to step 1 so they see the field with the error attached.
    if (!validateStep1()) {
      setCurrentStep(1);
      return;
    }
    if (!validateStep2()) return;

    try {
      console.log('[CreateSession] Sending time data:', {
        startDate: formData.startDate,
        startTime: formData.startTime,
        endTime: formData.endTime,
        timezone: formData.timezone
      });

      // Phase 2: build pre-session config (only if enabled — undefined otherwise so backend
      // leaves the schema defaults in place).
      const preSessionConfig = formData.preSessionEnabled ? {
        enabled: true,
        materials: [], // uploaded AFTER session creation, then attached via update
        selfAssessment: {
          enabled: formData.selfAssessmentEnabled,
          questions: formData.selfAssessmentQuestions.map((q, idx) => ({
            questionText: q.questionText,
            questionType: q.questionType,
            // Parse the raw `optionsText` at submit time so typing commas/spaces
            // mid-edit doesn't get eaten.
            options: q.optionsText !== undefined
              ? q.optionsText.split(',').map(s => s.trim()).filter(Boolean)
              : (q.options || []),
            order: idx,
          })),
        },
        challengeCapture: { enabled: formData.challengeCaptureEnabled },
        expectationCapture: { enabled: formData.expectationCaptureEnabled },
      } : undefined;

      // Phase 2: build post-session config (only if enabled).
      const postSessionConfig = formData.postSessionEnabled ? {
        enabled: true,
        actionPlanTracking: { enabled: formData.actionPlanTrackingEnabled },
        managerReinforcement: { enabled: formData.managerReinforcementEnabled },
        reflectionCycles: {
          enabled: formData.reflectionCyclesEnabled,
          intervals: formData.reflectionIntervals,
          questions: formData.reflectionQuestions.map((q, idx) => ({
            questionText: q.questionText,
            order: idx,
          })),
        },
      } : undefined;

      // Prepare session data for API - create session first without workbooks
      // Send raw time strings (HH:MM format) - no timezone conversion
      const sessionData = {
        name: formData.name,
        description: formData.description,
        sessionType: formData.sessionType,
        scheduledDate: formData.startDate, // Use startDate as the main scheduled date
        startDate: formData.startDate,
        endDate: formData.endDate || formData.startDate, // Default to startDate if not set
        // Send raw time strings - backend will create datetime without timezone conversion
        startTime: formData.startTime,
        endTime: formData.endTime,
        timezone: formData.timezone,
        trainer: formData.trainerId,
        expectedParticipants: formData.expectedParticipants,
        // Always use plan limit — field is read-only and user can't change it
        maxParticipants: isUnlimitedParticipants() ? 500 : getParticipantLimit(),
        workbooks: [], // Create without workbooks first
        // Overall session feedback settings
        overallFeedback: {
          enabled: formData.enableOverallFeedback,
        },
        // Phase 2: both undefined when user skips Step 3 — backend defaults apply
        preSession: preSessionConfig,
        postSession: postSessionConfig,
      };

      const response = await sessionService.createSession(sessionData);

      if (response.success) {
        const createdData = response.data;
        const sessionId = createdData._id;

        // Upload workbooks after session creation
        if (formData.workbooks.length > 0) {
          const uploadedWorkbooks = [];
          for (const wb of formData.workbooks) {
            if (wb.file) {
              try {
                const uploadResult = await clientService.uploadWorkbook(sessionId, wb.file);
                if (uploadResult.success) {
                  uploadedWorkbooks.push(uploadResult.data);
                }
              } catch (uploadError) {
                console.error('Error uploading workbook:', wb.name, uploadError);
              }
            }
          }

          // Update session with uploaded workbooks
          if (uploadedWorkbooks.length > 0) {
            console.log('Updating session with workbooks:', uploadedWorkbooks);
            const updateResult = await sessionService.updateSession(sessionId, {
              workbooks: uploadedWorkbooks,
            });
            console.log('Session update result:', updateResult);
          }
        }

        // Phase 2: upload pre-read materials and attach them to the session.
        // Use api.upload (goes through the api wrapper — handles baseURL, auth,
        // and 401 redirects). A raw fetch('/api/...') fails silently in dev
        // if no proxy is configured, leaving the session with no materials.
        if (formData.preSessionEnabled && formData.preSessionMaterials.length > 0) {
          const uploadedMaterials = [];
          for (const material of formData.preSessionMaterials) {
            if (material.file) {
              try {
                const fd = new FormData();
                fd.append('file', material.file);
                const result = await api.upload(`/upload/session/${sessionId}/pre-read`, fd);
                if (result?.success && result.data) {
                  uploadedMaterials.push(result.data);
                } else {
                  console.error('[pre-read upload] unsuccessful response:', result);
                }
              } catch (err) {
                console.error('Error uploading pre-read material:', material.name, err);
              }
            }
          }
          if (uploadedMaterials.length > 0) {
            await sessionService.updateSession(sessionId, {
              preSession: { ...preSessionConfig, materials: uploadedMaterials },
            });
          }
        }

        const session = {
          id: sessionId,
          ...formData,
          sessionCode: createdData.code,
          qrCode: `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${window.location.origin}/join/${createdData.code}`,
          status: createdData.status || 'scheduled',
          createdAt: createdData.createdAt,
        };

        setCreatedSession(session);
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error('Error creating session:', error);
      alert(error.response?.data?.message || 'Failed to create session. Please try again.');
    }
  };

  const generateSessionCode = () => {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    let code = '';
    for (let i = 0; i < 3; i++) code += letters.charAt(Math.floor(Math.random() * letters.length));
    for (let i = 0; i < 3; i++) code += numbers.charAt(Math.floor(Math.random() * numbers.length));
    return code;
  };

  const selectedTrainer = trainers.find((t) => t.id === formData.trainerId);

  // Workbook upload handlers - store File objects for later upload
  const handleWorkbookUpload = (e) => {
    const files = Array.from(e.target.files);
    const tier = user?.organization?.plan || user?.subscriptionTier || 'free';
    const fileLimit = { free: 2, pro: 50, pro_plus: 50, enterprise_starter: 50, enterprise_scale: 50 }[tier] || 2;

    if (formData.workbooks.length + files.length > fileLimit) {
      alert(`File limit reached. Maximum ${fileLimit} files allowed for your plan.`);
      return;
    }

    const newWorkbooks = files.map((file) => ({
      id: `workbook-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: file.name,
      size: file.size,
      type: file.type || file.name.split('.').pop() || 'unknown',
      previewUrl: URL.createObjectURL(file), // For local preview only
      file: file, // Store actual File object for upload
      uploadedAt: new Date().toISOString(),
    }));

    setFormData((prev) => ({
      ...prev,
      workbooks: [...prev.workbooks, ...newWorkbooks],
    }));

    // Reset input
    e.target.value = '';
  };

  const removeWorkbook = (id) => {
    setFormData((prev) => ({
      ...prev,
      workbooks: prev.workbooks.filter((w) => w.id !== id),
    }));
  };

  // Hard gate — if the plan is expired, render only the banner + a notice instead
  // of the full wizard. Backend would 403 the create call anyway; gating here
  // saves the user from filling out a form that can't be submitted.
  if (planStatus?.expired) {
    return (
      <DashboardLayout title="Create Session">
        <div className="p-4 max-w-4xl mx-auto pb-20 sm:pb-4">
          <PlanExpiredBanner audience="client" planEnd={planStatus.planEnd} upgradePath="/client/subscription" />
          <div className="bg-white rounded-xl border border-neutral-200 p-8 text-center">
            <h2 className="text-xl font-semibold text-neutral-900 mb-2">You can't create a session right now</h2>
            <p className="text-sm text-neutral-600 mb-6">
              Your plan has expired, so creating new sessions is paused.
            </p>
            <Button variant="primary" onClick={() => navigate('/client/subscription')}>
              Go to Subscription &amp; Renew
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Create Session">
      <div className="w-full px-4 lg:px-6 py-6 max-w-5xl mx-auto pb-20 sm:pb-6 space-y-5">
        {/* Back link */}
        <button
          type="button"
          onClick={() => handleBackNavigation('/client/sessions')}
          className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100 transition-colors group"
        >
          <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-0.5" />
          <span className="font-medium">Back to Sessions</span>
        </button>

        {/* Header */}
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">Create New Session</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">Set up your training session step by step</p>
        </div>

        {/* Progress Steps */}
        <div className="rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.04)] p-5">
          <div className="flex items-center justify-between">
            {[1, 2, 3].map((step) => {
              const done = step < currentStep;
              const active = step === currentStep;
              return (
                <div key={step} className="flex items-center flex-1 min-w-0">
                  <div className="flex items-center min-w-0">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all flex-shrink-0 ring-1 ring-inset ${
                        done
                          ? 'bg-emerald-500 text-white ring-emerald-500/40 shadow-[0_4px_12px_-4px_rgba(16,185,129,0.45)]'
                          : active
                          ? 'bg-primary-50 text-primary-700 ring-primary-200 dark:bg-primary-500/10 dark:text-primary-300 dark:ring-primary-500/30 shadow-[0_4px_12px_-4px_rgba(255,138,0,0.35)]'
                          : 'bg-neutral-50 text-neutral-500 ring-neutral-200 dark:bg-neutral-800 dark:text-neutral-500 dark:ring-neutral-700'
                      }`}
                    >
                      {done ? <Check size={16} strokeWidth={2.5} /> : step}
                    </div>
                    <div className="ml-3 hidden sm:block min-w-0">
                      <p className={`text-sm font-medium truncate ${
                        active
                          ? 'text-neutral-900 dark:text-neutral-100'
                          : done
                          ? 'text-neutral-700 dark:text-neutral-300'
                          : 'text-neutral-500 dark:text-neutral-500'
                      }`}>
                        {step === 1 ? 'Basic Info' : step === 2 ? 'Trainer & Participants' : 'Pre & Post Session'}
                      </p>
                    </div>
                  </div>
                  {step < 3 && (
                    <div className={`flex-1 h-0.5 mx-3 sm:mx-4 rounded-full ${
                      done ? 'bg-emerald-500' : 'bg-neutral-200 dark:bg-neutral-800'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          {/* Step 1: Basic Information */}
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <div className="p-6 space-y-6">
                  <div>
                    <h2 className="text-base sm:text-xl font-semibold text-secondary-900 mb-2">Basic Information</h2>
                    <p className="text-xs sm:text-sm text-neutral-600">Enter the basic details about your training session</p>
                  </div>

                  <Input
                    label="Session Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    error={errors.name}
                    placeholder="Leadership Training - Day 1"
                    required
                    fullWidth
                  />

                  <div>
                    <label className="block text-sm font-medium text-secondary-900 mb-2">Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="A comprehensive training on leadership principles..."
                      rows={4}
                      className="w-full px-4 py-2.5 text-sm text-secondary-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600/20 focus:border-primary-600 transition-all resize-none placeholder:text-neutral-400"
                    />
                  </div>

                  {/* Session Type - Clean outline cards */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-900 mb-3">Session Type</label>
                    <div className="space-y-3">
                      {/* Public Option */}
                      <div
                        onClick={() => handleChange({ target: { name: 'sessionType', value: 'public' } })}
                        className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          formData.sessionType === 'public'
                            ? 'border-primary-600 bg-primary-50'
                            : 'border-neutral-200 hover:border-neutral-300'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5">
                            <div
                              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                formData.sessionType === 'public'
                                  ? 'border-primary-600'
                                  : 'border-neutral-300'
                              }`}
                            >
                              {formData.sessionType === 'public' && (
                                <div className="w-2.5 h-2.5 bg-primary-600 rounded-full"></div>
                              )}
                            </div>
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-secondary-900">Public</p>
                            <p className="text-xs sm:text-sm text-neutral-600 mt-0.5">Anyone with code can join</p>
                          </div>
                        </div>
                      </div>

                      {/* Private Option (Coming Soon) */}
                      <div className="relative p-4 border-2 rounded-lg border-neutral-200 bg-neutral-50 opacity-60 cursor-not-allowed">
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5">
                            <div className="w-5 h-5 rounded-full border-2 border-neutral-300"></div>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-secondary-900">Private</p>
                              <span className="px-2 py-0.5 bg-white border border-warning text-warning text-xs font-medium rounded-full">
                                Coming Soon
                              </span>
                            </div>
                            <p className="text-xs sm:text-sm text-neutral-600 mt-0.5">Pre-registered participants only</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Start Date and End Date */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <Input
                      label="Start Date"
                      name="startDate"
                      type="date"
                      value={formData.startDate}
                      onChange={handleChange}
                      error={errors.startDate}
                      required
                      fullWidth
                    />
                    <div>
                      <Input
                        label={<>End Date <span className="text-neutral-400 text-xs font-normal">(optional)</span></>}
                        name="endDate"
                        type="date"
                        value={formData.endDate}
                        onChange={handleChange}
                        error={errors.endDate}
                        fullWidth
                        min={formData.startDate}
                      />
                      {!formData.endDate && formData.startDate && (
                        <p className="text-xs text-neutral-500 mt-1">Defaults to start date if not set</p>
                      )}
                    </div>
                  </div>

                  {/* Custom Time Picker with AM/PM - manual entry with spinner */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    {/* Start Time */}
                    <div>
                      <label className="block text-sm font-medium text-secondary-900 mb-2">
                        Start Time <span className="text-neutral-400 text-xs font-normal">(optional)</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          max="12"
                          value={timeData.startHour}
                          onChange={(e) => handleTimeChange('startHour', e.target.value)}
                          onBlur={(e) => handleTimeBlur('startHour', e.target.value)}
                          placeholder="HH"
                          className="w-16 px-2 py-2.5 text-sm text-center text-secondary-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600/20 focus:border-primary-600 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <span className="text-neutral-400 font-medium">:</span>
                        <input
                          type="number"
                          min="0"
                          max="59"
                          value={timeData.startMinute}
                          onChange={(e) => handleTimeChange('startMinute', e.target.value)}
                          onBlur={(e) => handleTimeBlur('startMinute', e.target.value)}
                          placeholder="MM"
                          className="w-16 px-2 py-2.5 text-sm text-center text-secondary-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600/20 focus:border-primary-600 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <select
                          value={timeData.startPeriod}
                          onChange={(e) => handleTimeChange('startPeriod', e.target.value)}
                          className="px-3 py-2.5 pr-8 text-sm font-medium text-secondary-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600/20 focus:border-primary-600 transition-all appearance-none cursor-pointer"
                          style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.25em 1.25em' }}
                        >
                          <option value="AM">AM</option>
                          <option value="PM">PM</option>
                        </select>
                      </div>
                      {errors.startTime && (
                        <p className="text-xs text-error mt-1">{errors.startTime}</p>
                      )}
                    </div>

                    {/* End Time */}
                    <div>
                      <label className="block text-sm font-medium text-secondary-900 mb-2">
                        End Time <span className="text-neutral-400 text-xs font-normal">(optional)</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          max="12"
                          value={timeData.endHour}
                          onChange={(e) => handleTimeChange('endHour', e.target.value)}
                          onBlur={(e) => handleTimeBlur('endHour', e.target.value)}
                          placeholder="HH"
                          className="w-16 px-2 py-2.5 text-sm text-center text-secondary-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600/20 focus:border-primary-600 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <span className="text-neutral-400 font-medium">:</span>
                        <input
                          type="number"
                          min="0"
                          max="59"
                          value={timeData.endMinute}
                          onChange={(e) => handleTimeChange('endMinute', e.target.value)}
                          onBlur={(e) => handleTimeBlur('endMinute', e.target.value)}
                          placeholder="MM"
                          className="w-16 px-2 py-2.5 text-sm text-center text-secondary-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600/20 focus:border-primary-600 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <select
                          value={timeData.endPeriod}
                          onChange={(e) => handleTimeChange('endPeriod', e.target.value)}
                          className="px-3 py-2.5 pr-8 text-sm font-medium text-secondary-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600/20 focus:border-primary-600 transition-all appearance-none cursor-pointer"
                          style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.25em 1.25em' }}
                        >
                          <option value="AM">AM</option>
                          <option value="PM">PM</option>
                        </select>
                      </div>
                      {errors.endTime && (
                        <p className="text-xs text-error mt-1">{errors.endTime}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-900 mb-2">Time Zone</label>
                    <select
                      name="timezone"
                      value={formData.timezone}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 pr-10 text-sm text-secondary-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600/20 focus:border-primary-600 transition-all appearance-none"
                      style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.75rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.25em 1.25em' }}
                    >
                      <option value="Asia/Kolkata">IST (UTC+05:30) India</option>
                    </select>
                  </div>

                  <div className="flex justify-end pt-4 border-t border-neutral-200">
                    <Button variant="primary-filled" size="lg" onClick={handleNext}
                      icon={
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                      }
                      iconPosition="right"
                    >
                      Next: Trainer
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Step 2: Trainer & Participants */}
          {currentStep === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <div className="p-6 space-y-6">
                  <div>
                    <h2 className="text-base sm:text-xl font-semibold text-secondary-900 mb-2">Trainer & Participants</h2>
                    <p className="text-xs sm:text-sm text-neutral-600">Assign a trainer and set participant capacity</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-900 mb-2">Assign Trainer *</label>
                    {errors.trainerId && <p className="text-xs text-error mb-2">{errors.trainerId}</p>}
                    <select
                      name="trainerId"
                      value={formData.trainerId}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 text-sm text-secondary-900 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600/20 focus:border-primary-600 transition-all ${
                        errors.trainerId ? 'border-error' : 'border-neutral-300'
                      }`}
                    >
                      <option value="">Select a trainer</option>
                      {trainers.map((trainer) => (
                        <option key={trainer.id} value={trainer.id}>
                          {trainer.fullName} {trainer.expertise.length > 0 ? `- ${trainer.expertise.slice(0, 2).join(', ')}` : ''} ({trainer.totalSessions} sessions)
                        </option>
                      ))}
                    </select>

                    {/* Selected Trainer Info Card */}
                    {selectedTrainer && (
                      <div className="mt-3 p-4 bg-primary-50 border border-primary-200 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center font-semibold border-2 border-primary-600 text-primary-600 bg-white">
                            {selectedTrainer.fullName.charAt(0)}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-secondary-900">{selectedTrainer.fullName}</p>
                            <p className="text-xs sm:text-sm text-neutral-600">{selectedTrainer.expertise.join(', ') || 'No expertise listed'}</p>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-xs text-neutral-500">★ {selectedTrainer.avgRating}/5</span>
                              <span className="text-xs text-neutral-500">{selectedTrainer.totalSessions} sessions</span>
                            </div>
                          </div>
                          <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </div>
                    )}

                    {trainers.length === 0 && (
                      <p className="mt-2 text-xs sm:text-sm text-neutral-500">No trainers available. Please add trainers to your organization first.</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="Expected Participants"
                      name="expectedParticipants"
                      type="number"
                      value={formData.expectedParticipants}
                      onChange={handleChange}
                      error={errors.expectedParticipants}
                      min={1}
                      max={isUnlimitedParticipants() ? 500 : getParticipantLimit()}
                      required
                      fullWidth
                    />
                    <div>
                      <Input
                        label="Maximum Capacity"
                        name="maxParticipants"
                        type="number"
                        value={isUnlimitedParticipants() ? 500 : getParticipantLimit()}
                        readOnly
                        disabled
                        fullWidth
                      />
                      <p className="text-xs text-neutral-500 mt-1.5">
                        {isUnlimitedParticipants() ? 'Unlimited as per your plan' : `Fixed at ${getParticipantLimit()} as per your plan`}
                      </p>
                    </div>
                  </div>

                  {/* Overall Session Feedback Toggle */}
                  <div className="pt-4 border-t border-neutral-200">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-secondary-900">Overall Session Feedback</label>
                        <p className="text-xs text-neutral-500 mt-0.5">
                          Collect feedback at the end of the entire session (after all subsessions)
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, enableOverallFeedback: !prev.enableOverallFeedback }))}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                          formData.enableOverallFeedback ? 'bg-primary-600' : 'bg-neutral-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            formData.enableOverallFeedback ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    {formData.enableOverallFeedback && (
                      <div className="mt-3 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-xs sm:text-sm text-primary-800">
                            Participants will be asked to rate the overall session and provide feedback after all subsessions are completed. This will be shown in session reports.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Optional Workbook Upload */}
                  <div className="pt-4 border-t border-neutral-200">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <label className="block text-sm font-medium text-secondary-900">Workbooks (Optional)</label>
                        <p className="text-xs text-neutral-500 mt-0.5">Upload training materials for participants</p>
                        {/* nginx rejects an oversized upload before the API sees it, so the
                            failure reads as a generic server error — state the limit here. */}
                        <p className="text-xs text-neutral-500 mt-0.5">Max {MAX_UPLOAD_MB} MB per file</p>
                      </div>
                      <span className="px-2 py-0.5 bg-neutral-100 text-neutral-600 text-xs font-medium rounded">
                        {formData.workbooks.length}/{({ free: 2, pro: 50, pro_plus: 50, enterprise_starter: 50, enterprise_scale: 50 }[user?.organization?.plan || user?.subscriptionTier] || 2)} files
                      </span>
                    </div>

                    {/* Uploaded Files List */}
                    {formData.workbooks.length > 0 && (
                      <div className="space-y-2 mb-4">
                        {formData.workbooks.map((file) => (
                          <div
                            key={file.id}
                            className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg border border-neutral-200"
                          >
                            <div className="flex items-center gap-3">
                              <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                              <div>
                                <p className="font-medium text-secondary-900 text-xs sm:text-sm">{file.name}</p>
                                <p className="text-xs text-neutral-500">
                                  {file.size && file.size > 0
                                    ? `${(file.size / 1024 / 1024).toFixed(2)} MB`
                                    : file.file?.size
                                      ? `${(file.file.size / 1024 / 1024).toFixed(2)} MB`
                                      : 'Size unknown'}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => removeWorkbook(file.id)}
                              className="p-2 text-error hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* File Type Notice */}
                    <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                      <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-xs sm:text-sm text-amber-800">
                        <span className="font-medium">Accepted formats:</span> PDF, PPT, PPTX only
                      </p>
                    </div>

                    {/* Upload Area */}
                    <div className="border-2 border-dashed border-neutral-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors">
                      <input
                        id="workbook-upload"
                        type="file"
                        multiple
                        accept=".pdf,.ppt,.pptx"
                        onChange={handleWorkbookUpload}
                        className="hidden"
                        disabled={formData.workbooks.length >= ({ free: 2, pro: 50, pro_plus: 50, enterprise_starter: 50, enterprise_scale: 50 }[user?.organization?.plan || user?.subscriptionTier] || 2)}
                      />
                      <label htmlFor="workbook-upload" className="cursor-pointer">
                        <svg className="w-10 h-10 mx-auto text-neutral-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z" />
                        </svg>
                        <p className="text-xs sm:text-sm text-neutral-600 mb-1">
                          {formData.workbooks.length === 0
                            ? 'Click to upload or drag and drop'
                            : 'Click to add more files'}
                        </p>
                        <p className="text-xs text-neutral-500">Max {({ free: 2, pro: 50, pro_plus: 50, enterprise_starter: 50, enterprise_scale: 50 }[user?.organization?.plan || user?.subscriptionTier] || 2)} files</p>
                      </label>
                    </div>
                  </div>

                  <div className="flex justify-between pt-4 border-t border-neutral-200">
                    <Button variant="ghost" onClick={handleBack}
                      icon={
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                        </svg>
                      }
                    >
                      Back
                    </Button>
                    <Button variant="primary-filled" size="lg" onClick={handleNext}
                      icon={
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                      }
                      iconPosition="right"
                    >
                      Next: Pre & Post Session
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Step 3: Pre & Post Session Configuration (Phase 2) */}
          {currentStep === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-5"
            >
              {/* Header (inline, no card) */}
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-secondary-900 mb-1">
                  Pre & Post Session Features
                </h2>
                <p className="text-sm text-neutral-600">
                  Optional features to enhance learning before and after training. Skip this step if not needed.
                </p>
              </div>

              {/* Pre-Session card (blue tint) */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 sm:p-6 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-base sm:text-lg font-semibold text-secondary-900">Enable Pre-Session</h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      Participants complete tasks before the training day
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 mt-1">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={formData.preSessionEnabled}
                      onChange={(e) => setFormData({ ...formData, preSessionEnabled: e.target.checked })}
                    />
                    <div className="w-11 h-6 bg-neutral-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>

                {formData.preSessionEnabled && (
                  <div className="pt-4 border-t border-blue-200 space-y-4">
                      {/* Materials upload */}
                      <div>
                        <label className="block text-sm font-medium text-secondary-900 mb-2">Pre-Read Materials</label>
                        <input
                          type="file"
                          multiple
                          accept=".pdf,.ppt,.pptx,.doc,.docx,image/*,video/*"
                          onChange={(e) => {
                            const files = Array.from(e.target.files).map(f => ({
                              file: f,
                              name: f.name,
                              size: f.size,
                              type: f.type,
                            }));
                            setFormData({
                              ...formData,
                              preSessionMaterials: [...formData.preSessionMaterials, ...files],
                            });
                            e.target.value = '';
                          }}
                          className="block w-full text-sm text-neutral-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                        />
                        {formData.preSessionMaterials.length > 0 && (
                          <ul className="mt-2 space-y-1">
                            {formData.preSessionMaterials.map((m, idx) => (
                              <li key={idx} className="flex items-center justify-between text-sm bg-neutral-50 px-3 py-2 rounded">
                                <span className="truncate">{m.name}</span>
                                <button
                                  type="button"
                                  onClick={() => setFormData({
                                    ...formData,
                                    preSessionMaterials: formData.preSessionMaterials.filter((_, i) => i !== idx),
                                  })}
                                  className="text-red-500 hover:text-red-700 ml-2"
                                >
                                  ✕
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      {/* Self-assessment toggle */}
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-secondary-900">Self-Assessment</label>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={formData.selfAssessmentEnabled}
                            onChange={(e) => setFormData({ ...formData, selfAssessmentEnabled: e.target.checked })}
                          />
                          <div className="w-9 h-5 bg-neutral-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                      </div>

                      {formData.selfAssessmentEnabled && (
                        <div className="pl-4 border-l-2 border-neutral-100 space-y-3">
                          {formData.selfAssessmentQuestions.map((q, idx) => (
                            <div key={idx} className="bg-neutral-50 p-3 rounded space-y-2">
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  placeholder="Question text"
                                  value={q.questionText}
                                  onChange={(e) => {
                                    const updated = [...formData.selfAssessmentQuestions];
                                    updated[idx] = { ...q, questionText: e.target.value };
                                    setFormData({ ...formData, selfAssessmentQuestions: updated });
                                  }}
                                  className="flex-1 px-3 py-2 border border-neutral-200 rounded text-sm"
                                />
                                <select
                                  value={q.questionType}
                                  onChange={(e) => {
                                    const updated = [...formData.selfAssessmentQuestions];
                                    updated[idx] = { ...q, questionType: e.target.value };
                                    setFormData({ ...formData, selfAssessmentQuestions: updated });
                                  }}
                                  className="px-3 py-2 border border-neutral-200 rounded text-sm"
                                >
                                  <option value="rating">Rating (1-5)</option>
                                  <option value="text">Text</option>
                                  <option value="mcq">Multiple Choice</option>
                                </select>
                                <button
                                  type="button"
                                  onClick={() => setFormData({
                                    ...formData,
                                    selfAssessmentQuestions: formData.selfAssessmentQuestions.filter((_, i) => i !== idx),
                                  })}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  ✕
                                </button>
                              </div>
                              {q.questionType === 'mcq' && (
                                <input
                                  type="text"
                                  placeholder="Options (comma-separated)"
                                  // Keep the raw input in `optionsText` — splitting/trimming on
                                  // every keystroke eats commas and spaces mid-type. Parsed into
                                  // the final `options` array at submit time.
                                  value={q.optionsText !== undefined ? q.optionsText : (q.options || []).join(', ')}
                                  onChange={(e) => {
                                    const updated = [...formData.selfAssessmentQuestions];
                                    updated[idx] = { ...q, optionsText: e.target.value };
                                    setFormData({ ...formData, selfAssessmentQuestions: updated });
                                  }}
                                  className="w-full px-3 py-2 border border-neutral-200 rounded text-sm"
                                />
                              )}
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setFormData({
                              ...formData,
                              selfAssessmentQuestions: [
                                ...formData.selfAssessmentQuestions,
                                { questionText: '', questionType: 'rating', options: [], optionsText: '' },
                              ],
                            })}
                          >
                            + Add Question
                          </Button>
                        </div>
                      )}

                      {/* Challenge capture */}
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-secondary-900">Challenge Capture</label>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={formData.challengeCaptureEnabled}
                            onChange={(e) => setFormData({ ...formData, challengeCaptureEnabled: e.target.checked })}
                          />
                          <div className="w-9 h-5 bg-neutral-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                      </div>

                      {/* Expectation capture */}
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-secondary-900">Expectation Capture</label>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={formData.expectationCaptureEnabled}
                            onChange={(e) => setFormData({ ...formData, expectationCaptureEnabled: e.target.checked })}
                          />
                          <div className="w-9 h-5 bg-neutral-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                      </div>
                  </div>
                )}
              </div>

              {/* Post-Session card (green tint) */}
              <div className="bg-green-50 border border-green-100 rounded-xl p-5 sm:p-6 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-base sm:text-lg font-semibold text-secondary-900">Enable Post-Session Tracking</h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      Participants track action plans and reflect after training
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 mt-1">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={formData.postSessionEnabled}
                      onChange={(e) => setFormData({ ...formData, postSessionEnabled: e.target.checked })}
                    />
                    <div className="w-11 h-6 bg-neutral-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>

                {formData.postSessionEnabled && (
                  <div className="pt-4 border-t border-green-200 space-y-4">
                      {/* Action plan tracking */}
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-secondary-900">Action Plan Tracking</label>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={formData.actionPlanTrackingEnabled}
                            onChange={(e) => setFormData({ ...formData, actionPlanTrackingEnabled: e.target.checked })}
                          />
                          <div className="w-9 h-5 bg-neutral-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                      </div>

                      {/* Manager reinforcement */}
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-secondary-900">Manager Reinforcement</label>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={formData.managerReinforcementEnabled}
                            onChange={(e) => setFormData({ ...formData, managerReinforcementEnabled: e.target.checked })}
                          />
                          <div className="w-9 h-5 bg-neutral-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                      </div>

                      {/* Reflection cycles */}
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-secondary-900">Reflection Cycles</label>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={formData.reflectionCyclesEnabled}
                            onChange={(e) => setFormData({ ...formData, reflectionCyclesEnabled: e.target.checked })}
                          />
                          <div className="w-9 h-5 bg-neutral-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                      </div>

                    {formData.reflectionCyclesEnabled && (
                      <div className="pl-4 border-l-2 border-green-200 space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-secondary-900 mb-1">
                            Reflection Intervals (days after session)
                          </label>
                          <input
                            type="text"
                            value={reflectionIntervalsText}
                            onChange={(e) => {
                              // Show exactly what was typed; parse alongside it rather
                              // than feeding the parsed value back into the field.
                              setReflectionIntervalsText(e.target.value);
                              const intervals = e.target.value
                                .split(',')
                                .map(s => parseInt(s.trim(), 10))
                                .filter(n => Number.isFinite(n) && n > 0);
                              setFormData({ ...formData, reflectionIntervals: intervals });
                            }}
                            // Tidy up on exit: drops stray separators and whitespace
                            // without interfering mid-entry.
                            onBlur={() => setReflectionIntervalsText(formData.reflectionIntervals.join(', '))}
                            placeholder="30, 60, 90"
                            className="w-full px-3 py-2 border border-neutral-200 rounded text-sm bg-white"
                          />
                          <p className="text-xs text-neutral-500 mt-1">Comma-separated numbers. Default: 30, 60, 90.</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-secondary-900 mb-2">
                            Reflection Questions
                          </label>
                          {/* Hint — clarifies that custom questions are optional;
                              participants get a usable default set if this is left blank. */}
                          <div className="mb-3 rounded-lg bg-blue-50 ring-1 ring-inset ring-blue-200/70 px-3 py-2 text-xs text-blue-800">
                            <span className="font-medium">Tip:</span> Add your own reflection prompts below, or leave this empty to use the standard 4 questions —
                            <em className="not-italic"> "What action did you implement?", "What result did you observe?", "What challenge remains?", "What support do you need?"</em>
                          </div>
                          {formData.reflectionQuestions.map((q, idx) => (
                            <div key={idx} className="flex items-center gap-2 mb-2">
                              <input
                                type="text"
                                value={q.questionText}
                                onChange={(e) => {
                                  const updated = [...formData.reflectionQuestions];
                                  updated[idx] = { ...q, questionText: e.target.value };
                                  setFormData({ ...formData, reflectionQuestions: updated });
                                }}
                                className="flex-1 px-3 py-2 border border-neutral-200 rounded text-sm bg-white"
                              />
                              <button
                                type="button"
                                onClick={() => setFormData({
                                  ...formData,
                                  reflectionQuestions: formData.reflectionQuestions.filter((_, i) => i !== idx),
                                })}
                                className="text-red-500 hover:text-red-700"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setFormData({
                              ...formData,
                              reflectionQuestions: [
                                ...formData.reflectionQuestions,
                                { questionText: '', order: formData.reflectionQuestions.length },
                              ],
                            })}
                          >
                            + Add Question
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Info note */}
              <div className="bg-neutral-50 border border-neutral-200 rounded-lg px-5 py-4 text-center">
                <p className="text-sm text-neutral-600">
                  Pre & Post session features are optional. You can skip this step and the session will work exactly as before.
                </p>
              </div>

              {/* Navigation buttons */}
              <div className="flex justify-between pt-2">
                <Button variant="ghost" onClick={handleBack}
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                  }
                >
                  Back
                </Button>
                <Button type="button" variant="primary-filled" size="lg" onClick={handleCreateSession}>
                  Create Session
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Success Modal - Clean design */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => navigate('/client/sessions')}
        title=""
        size="lg"
      >
        {createdSession && (
          <div className="space-y-6">
            {/* Success Icon */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 border-2 border-success rounded-full mb-4">
                <svg className="w-8 h-8 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl sm:text-2xl font-semibold text-secondary-900">Session Created!</h3>
              <p className="text-neutral-600 mt-2">Your training session has been scheduled successfully</p>
            </div>

            {/* Session Details */}
            <div className="border border-neutral-200 rounded-lg p-5 space-y-4">
              <div>
                <h4 className="text-sm sm:text-lg font-medium text-secondary-900">{createdSession.name}</h4>
                <p className="text-xs sm:text-sm text-neutral-600 mt-1">
                  {new Date(createdSession.startDate || createdSession.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  {createdSession.endDate && createdSession.endDate !== createdSession.startDate && (
                    <> - {new Date(createdSession.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</>
                  )}
                  {createdSession.startTime && <> • {createdSession.startTime}{createdSession.endTime && ` - ${createdSession.endTime}`}</>}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-neutral-200">
                <div>
                  <p className="text-sm font-medium text-neutral-700 mb-2">Session Code</p>
                  <div className="flex items-center gap-2">
                    <p className="text-xl sm:text-2xl font-semibold text-primary-600">{createdSession.sessionCode}</p>
                    <button
                      onClick={() => {
                        const code = createdSession.sessionCode;
                        if (navigator.clipboard && navigator.clipboard.writeText) {
                          navigator.clipboard.writeText(code);
                        } else {
                          // Fallback for non-HTTPS contexts
                          const textArea = document.createElement('textarea');
                          textArea.value = code;
                          document.body.appendChild(textArea);
                          textArea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textArea);
                        }
                      }}
                      className="p-1.5 text-neutral-500 hover:text-secondary-900 hover:bg-neutral-100 rounded transition-colors"
                      title="Copy code"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-neutral-700 mb-2">QR Code</p>
                  <img src={createdSession.qrCode} alt="QR Code" className="w-24 h-24 border border-neutral-200 rounded-lg" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-neutral-200">
                <div>
                  <p className="text-sm font-medium text-neutral-700 mb-1">Assigned Trainer</p>
                  <p className="text-xs sm:text-sm text-secondary-900">{selectedTrainer?.fullName}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-neutral-700 mb-1">Capacity</p>
                  <p className="text-xs sm:text-sm text-secondary-900">{createdSession.maxParticipants} participants</p>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-blue-50 border border-info rounded-lg p-4">
              <p className="text-sm font-medium text-info mb-2 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Next Steps:
              </p>
              <ol className="text-xs sm:text-sm text-info space-y-1 list-decimal list-inside">
                <li>Assigned trainer will add and configure activities</li>
                <li>Share session code/QR with participants</li>
                <li>Start session on scheduled time</li>
              </ol>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button variant="primary-filled" fullWidth onClick={() => navigate(`/client/sessions/${createdSession.id}`)}>
                View Session Details
              </Button>
              <Button variant="secondary" fullWidth onClick={() => navigate('/client/sessions')}>
                Back to Sessions
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Unsaved Changes Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showBackConfirm}
        onClose={() => setShowBackConfirm(false)}
        onConfirm={confirmLeave}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave without saving?"
        confirmText="Yes, Leave"
        cancelText="No, Stay"
        variant="warning"
      />
    </DashboardLayout>
  );
};

export default CreateSessionPage;
